import { useRef } from "react";
import { motion, useMotionValue, useScroll, useSpring, useTransform, useMotionTemplate } from "framer-motion";
import { Phone, MessageSquare, ArrowDownRight } from "lucide-react";
import { BIZ } from "@/data/business";
import { MaskLines, EASE } from "./motion";
import { INTRO_DELAY as D } from "./Intro";
import { scrollToId } from "./SmoothScroll";

const Badge3D = ({ rx, ry }) => (
  <motion.div style={{ rotateX: rx, rotateY: ry, transformPerspective: 1000 }} className="relative will-change-transform">
    <div className="absolute inset-[8%] rounded-full bg-[#1bb4f2]/25 blur-[70px]" />
    <motion.img
      src="/velocity-badge.png" alt="Velocity Detailing — Gloss Boss on Wheels" data-testid="hero-badge"
      initial={{ scale: 0.6, opacity: 0, rotate: -20 }} animate={{ scale: 1, opacity: 1, rotate: 0 }}
      transition={{ duration: 1.6, ease: EASE, delay: D + 0.4 }}
      className="relative w-full drop-shadow-[0_30px_60px_rgba(27,180,242,0.25)]"
    />
  </motion.div>
);

export const Hero = () => {
  const ref = useRef(null);
  const mx = useMotionValue(0.5);
  const my = useMotionValue(0.4);
  const sx = useSpring(mx, { stiffness: 80, damping: 20 });
  const sy = useSpring(my, { stiffness: 80, damping: 20 });
  const rx = useTransform(sy, [0, 1], [14, -14]);
  const ry = useTransform(sx, [0, 1], [-18, 18]);
  const lx = useTransform(sx, (v) => `${v * 100}%`);
  const ly = useTransform(sy, (v) => `${v * 100}%`);
  const spot = useMotionTemplate`radial-gradient(520px circle at ${lx} ${ly}, rgba(27,180,242,0.22), transparent 60%)`;
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.2]);
  const titleY = useTransform(scrollYProgress, [0, 1], ["0%", "-30%"]);

  const onMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width);
    my.set((e.clientY - r.top) / r.height);
  };

  return (
    <section id="top" ref={ref} onMouseMove={onMove} data-testid="hero-section" className="relative min-h-[100svh] overflow-hidden grain">
      <motion.div style={{ y: bgY, scale: bgScale }} className="absolute inset-0">
        <img src="/img/silhouette.jpg" alt="Black car silhouette under a studio light" className="h-full w-full object-cover object-center opacity-70" />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-b from-[#07080B]/70 via-[#07080B]/30 to-[#07080B]" />
      <motion.div className="absolute inset-0 pointer-events-none" style={{ background: spot }} />
      <motion.div initial={{ scaleX: 0 }} animate={{ scaleX: 1 }} transition={{ duration: 1.8, ease: EASE, delay: D + 0.2 }} className="absolute left-0 right-0 top-[58%] hairline origin-left" />

      <div className="relative mx-auto max-w-[1440px] px-5 md:px-10 pt-28 md:pt-36 pb-16 grid lg:grid-cols-12 gap-10 items-center min-h-[100svh]">
        <motion.div style={{ y: titleY }} className="lg:col-span-7 order-2 lg:order-1">
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: D + 0.3, duration: 1 }} className="eyebrow mb-6 flex items-center gap-3">
            <span className="h-px w-10 bg-[#1bb4f2]" />Vernon, BC · Detailing & Protection Studio
          </motion.p>
          <h1 className="font-display font-black uppercase leading-[0.9] tracking-[-0.02em] text-[8.2vw] sm:text-6xl lg:text-[4.3rem] xl:text-[4.9rem]">
            <MaskLines lines={["Exceptional", "care."]} lineClass="chrome-text" delay={D + 0.35} />
            <MaskLines lines={["Down to", "the detail."]} lineClass="vel-text" delay={D + 0.59} />
          </h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: D + 1.0, duration: 1, ease: EASE }} className="mt-8 max-w-xl text-base md:text-lg text-slate-300 leading-relaxed" data-testid="hero-intro">
            Velocity Detailing is a locally owned detailing and protection studio in Vernon, serving drivers, boat owners and RV enthusiasts across the Okanagan.
          </motion.p>
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: D + 1.1, duration: 1 }} className="mt-5 max-w-xl font-mono text-[11px] md:text-xs uppercase tracking-[0.18em] text-[#6fd6ff] leading-relaxed" data-testid="hero-services-line">
            Detailing · Paint Correction · Ceramic Coatings · Window Tint · Paint Protection Film · Marine & RV Care
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: D + 1.15, duration: 1, ease: EASE }} className="mt-10 flex flex-wrap gap-3">
            <button onClick={() => scrollToId("quote")} data-testid="hero-quote-cta" className="group inline-flex items-center gap-3 h-14 px-7 bg-[#1bb4f2] text-[#07080B] font-semibold hover:bg-white transition-colors">
              Request a Quote <ArrowDownRight className="h-5 w-5 transition-transform duration-300 group-hover:rotate-[-45deg]" />
            </button>
            <a href={BIZ.tel} data-testid="hero-call-cta" className="inline-flex items-center gap-3 h-14 px-6 border border-white/20 hover:border-[#1bb4f2] hover:bg-white/5 transition-colors"><Phone className="h-4 w-4 text-[#1bb4f2]" />Call {BIZ.phone}</a>
            <a href={BIZ.sms} data-testid="hero-text-cta" className="inline-flex items-center gap-3 h-14 px-6 border border-white/20 hover:border-[#1bb4f2] hover:bg-white/5 transition-colors"><MessageSquare className="h-4 w-4 text-[#1bb4f2]" />Text us</a>
          </motion.div>
        </motion.div>
        <div className="lg:col-span-5 order-1 lg:order-2 mx-auto w-[62%] sm:w-[48%] lg:w-full max-w-[520px]">
          <Badge3D rx={rx} ry={ry} />
        </div>
      </div>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: D + 1.6 }} className="absolute bottom-6 left-5 md:left-10 right-5 md:right-10 hidden md:flex justify-between font-mono text-[11px] uppercase tracking-[0.25em] text-slate-400">
        <span>Automotive · Marine · RV</span><span>Pickup & drop-off — Vernon · Coldstream</span><span>Scroll ↓</span>
      </motion.div>
    </section>
  );
};
